import { useEffect, useRef } from "react";
import { ArrowRight, ArrowLeft } from "lucide-react";
import LocalizedLink from "@/components/LocalizedLink";
import { useLanguage } from "@/i18n/LanguageContext";
import { trackEvent } from "@/analytics/ga";

/**
 * Global SAS V2 announcement bar (Phase 24).
 *
 * Mounts once in `Layout.jsx` above the primary `<Navbar />`, so it
 * appears on every public route consistently (English at `/`, Arabic at
 * `/ar/*`, tool detail pages, articles, contact flow, etc.).
 *
 * Design constraints from `SAS_V2_Launch_Signal_UX_Implementation_Map`:
 *   - ~40–44 px compact height, one-line desktop / two-line phone.
 *   - Small "V2" badge with a restrained shimmer/pulse; the sentence
 *     itself never animates.
 *   - Light + dark + LTR + RTL.
 *   - Zero horizontal overflow.
 *   - No new animation package.
 *   - Respects `prefers-reduced-motion`.
 *   - Links to the V2 preview article (`/v2` in EN, `/ar/v2` in AR).
 *
 * Analytics (spec: safe parameters only — no PII, no free text):
 *   - `v2_banner_view` — fired ONCE per browser session using
 *     sessionStorage as the dedupe key. Prevents inflating impressions
 *     when the user navigates between routes inside the same session.
 *   - `v2_banner_click` — fired on every click on the bar.
 */

const SESSION_VIEW_KEY = "sas_v2_banner_view_fired";

/*
 * Copy (Phase 24 UX refinement): the bar is a *release signal*, not an
 * explanation. Short, curiosity-driven, one line on desktop. The trailing
 * arrow glyph from the brief (← / →) is rendered by the lucide icon below
 * so it is never duplicated in the text node.
 */
const COPY = {
  ar: {
    badge: "V2",
    text: "SAS V2 قادم — اكتشف ما الذي سيتغيّر",
    // Arabic reading order — the arrow points visually to the previous
    // item in RTL, i.e. ← is the "forward" glyph in Arabic UI.
    arrow: "left",
    aria: "شريط إعلان — SAS V2",
  },
  en: {
    badge: "V2",
    text: "SAS V2 is coming — Discover what is changing",
    arrow: "right",
    aria: "SAS V2 announcement",
  },
};

export default function V2AnnouncementBar() {
  const { lang } = useLanguage();
  const copy = COPY[lang] || COPY.en;
  const hasFiredView = useRef(false);

  /* Once-per-session impression event. */
  useEffect(() => {
    if (hasFiredView.current) return;
    if (typeof window === "undefined") return;
    try {
      const already = window.sessionStorage.getItem(SESSION_VIEW_KEY);
      if (already) {
        hasFiredView.current = true;
        return;
      }
      window.sessionStorage.setItem(SESSION_VIEW_KEY, "1");
    } catch {
      // sessionStorage can be disabled (private mode, quota). We accept
      // over-counting in that edge case rather than failing to record.
    }
    hasFiredView.current = true;
    trackEvent("v2_banner_view", { language: lang });
    // Intentionally NOT depending on `lang` — this is a per-session
    // metric; a language switch inside the same visit is one impression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onClick = () => {
    trackEvent("v2_banner_click", { language: lang });
  };

  const ArrowIcon = copy.arrow === "left" ? ArrowLeft : ArrowRight;

  return (
    <div
      className="v2-bar w-full border-b border-[color:var(--sas-line)] bg-white dark:bg-[#0e0e12]"
      role="region"
      aria-label={copy.aria}
      data-testid="v2-announcement-bar"
    >
      <LocalizedLink
        to="/v2"
        onClick={onClick}
        // The whole bar is one big link so the tap target is generous
        // on mobile. Keyboard users get a single focus stop.
        className="v2-bar-link flex items-center justify-center gap-2 sm:gap-3 px-4 py-2 min-h-[43px] text-[12.5px] sm:text-[13px] leading-snug text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--sas-accent)]/50"
        data-testid="v2-announcement-link"
      >
        <span
          className="v2-badge inline-flex items-center justify-center min-w-[26px] h-[20px] px-1.5 rounded-full text-[10.5px] font-bold tracking-wide"
          aria-hidden
        >
          {copy.badge}
        </span>
        <span className="text-[color:var(--sas-ink)] font-medium">
          {copy.text}
        </span>
        <ArrowIcon
          size={14}
          className="text-[color:var(--sas-accent)] shrink-0"
          aria-hidden
        />
      </LocalizedLink>
    </div>
  );
}
